import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Hook para captura de áudio do microfone e cálculo de decibéis
 * Usa Web Audio API para análise em tempo real
 * - Exibição visual atualizada a cada 1 segundo
 * - Gravação de amostras: valor máximo a cada 5 segundos
 */
export const useAudioCapture = (calibrationOffset = 0) => {
  const [isRecording, setIsRecording] = useState(false);
  const [currentDb, setCurrentDb] = useState(0);
  const [peakDb, setPeakDb] = useState(0);
  const [samples, setSamples] = useState([]);
  const [error, setError] = useState(null);
  const [frequencyData, setFrequencyData] = useState([]);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const displayIntervalRef = useRef(null);
  const sampleIntervalRef = useRef(null);
  const samplesRef = useRef([]);
  
  // Referências para tracking do máximo no período de 5 segundos
  const maxDbInWindowRef = useRef(0);
  const currentDbRef = useRef(0);

  /**
   * Calcula o nível de pressão sonora em dB a partir dos dados de áudio
   */
  const calculateDb = useCallback((dataArray) => {
    // Calcula RMS (Root Mean Square)
    let sumSquares = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = (dataArray[i] - 128) / 128; // Normaliza de 0-255 para -1 a 1
      sumSquares += normalized * normalized;
    }
    const rms = Math.sqrt(sumSquares / dataArray.length);
    
    // Converte para dB (20 * log10(rms))
    // Adiciona offset para aproximar valores reais de SPL
    // O offset de calibração ajusta para o valor real
    const dbValue = rms > 0 
      ? 20 * Math.log10(rms) + 90 + calibrationOffset 
      : 0;
    
    return Math.max(0, Math.min(140, dbValue)); // Limita entre 0 e 140 dB
  }, [calibrationOffset]);

  /**
   * Lê dados do analisador e retorna dB atual (usado internamente)
   */
  const readCurrentDb = useCallback(() => {
    if (!analyserRef.current) return 0;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);
    return calculateDb(dataArray);
  }, [calculateDb]);

  /**
   * Atualiza dados de frequência para análise tonal
   */
  const updateFrequencyData = useCallback(() => {
    if (!analyserRef.current || !audioContextRef.current) return;

    const freqArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(freqArray);
    
    // Converte para bandas de 1/3 de oitava (simplificado)
    const bands = [];
    const sampleRate = audioContextRef.current.sampleRate || 44100;
    const binSize = sampleRate / (analyserRef.current.fftSize || 2048);
    
    // Frequências centrais de 1/3 de oitava (31.5 Hz a 16 kHz)
    const centerFreqs = [31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 
                        500, 630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 
                        5000, 6300, 8000, 10000, 12500, 16000];
    
    centerFreqs.forEach(freq => {
      const binIndex = Math.round(freq / binSize);
      if (binIndex < freqArray.length) {
        const value = freqArray[binIndex];
        const dbValue = value > 0 ? 20 * Math.log10(value / 255) + 90 + calibrationOffset : 0;
        bands.push({ freq, db: Math.max(0, dbValue) });
      }
    });
    
    setFrequencyData(bands);
  }, [calibrationOffset]);

  /**
   * Inicia a gravação/captura de áudio
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      
      // Solicita permissão do microfone
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      
      // Cria contexto de áudio
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
      
      // Cria analisador
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      analyserRef.current.smoothingTimeConstant = 0.3;
      
      // Conecta microfone ao analisador
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      // Reseta amostras e valores
      samplesRef.current = [];
      setSamples([]);
      setPeakDb(0);
      maxDbInWindowRef.current = 0;
      currentDbRef.current = 0;
      
      setIsRecording(true);
      
      // Intervalo de EXIBIÇÃO: atualiza display a cada 1 segundo
      displayIntervalRef.current = setInterval(() => {
        const db = readCurrentDb();
        currentDbRef.current = db;
        setCurrentDb(db);
        
        // Atualiza pico geral
        setPeakDb(prev => Math.max(prev, db));
        
        // Atualiza máximo da janela de 5 segundos
        maxDbInWindowRef.current = Math.max(maxDbInWindowRef.current, db);
        
        // Atualiza dados de frequência
        updateFrequencyData();
      }, 1000); // Atualiza exibição a cada 1 segundo
      
      // Intervalo de GRAVAÇÃO: grava máximo a cada 5 segundos
      sampleIntervalRef.current = setInterval(() => {
        const timestamp = Date.now();
        const maxDb = maxDbInWindowRef.current;
        
        // Só grava se houver valor significativo
        if (maxDb > 0) {
          samplesRef.current.push({ timestamp, db: maxDb });
          setSamples([...samplesRef.current]);
        }
        
        // Reseta máximo da janela para próximo período
        maxDbInWindowRef.current = 0;
      }, 5000); // Grava máximo a cada 5 segundos
      
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      setError(err.message || 'Erro ao acessar microfone. Verifique as permissões.');
    }
  }, [readCurrentDb, updateFrequencyData]);

  /**
   * Para a gravação/captura
   */
  const stopRecording = useCallback(() => {
    // Limpa intervalos de exibição e gravação
    if (displayIntervalRef.current) {
      clearInterval(displayIntervalRef.current);
      displayIntervalRef.current = null;
    }
    
    if (sampleIntervalRef.current) {
      clearInterval(sampleIntervalRef.current);
      sampleIntervalRef.current = null;
    }
    
    // Grava a última amostra se houver máximo pendente
    if (maxDbInWindowRef.current > 0) {
      const timestamp = Date.now();
      samplesRef.current.push({ timestamp, db: maxDbInWindowRef.current });
      setSamples([...samplesRef.current]);
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    setIsRecording(false);
    
    // Retorna cópia das amostras coletadas
    return [...samplesRef.current];
  }, []);

  /**
   * Reseta as amostras e pico
   */
  const resetSamples = useCallback(() => {
    samplesRef.current = [];
    setSamples([]);
    setPeakDb(0);
    setCurrentDb(0);
  }, []);

  /**
   * Cleanup ao desmontar
   */
  useEffect(() => {
    return () => {
      if (displayIntervalRef.current) {
        clearInterval(displayIntervalRef.current);
      }
      if (sampleIntervalRef.current) {
        clearInterval(sampleIntervalRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return {
    isRecording,
    currentDb,
    peakDb,
    samples,
    frequencyData,
    error,
    startRecording,
    stopRecording,
    resetSamples
  };
};

export default useAudioCapture;
